import {
  Button,
  Checkbox,
  DatePicker,
  DateTimePicker,
  Icon,
  Input,
  MarkdownEditor,
  Menu,
  PageHeader,
  Select,
  useConfirm,
  useToast,
} from '@/components';
import { localDateTime, validDateTime } from '@/components/DateTimePicker';
import { apiCall } from '@/lib/api';
import { useBeforeLeave } from '@solidjs/router';
import { useQueryClient } from '@tanstack/solid-query';
import {
  ApiClientError,
  POLL_IMAGE_CONTENT_TYPES,
  POLL_IMAGE_MAX_BYTES,
  PollSlugSchema,
} from '@uchat-team-management/shared';
import { For, Show, createEffect, createSignal, onCleanup } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { PollPreview } from './PollPreview';
import { PollDisplayElement, PollQuestion } from './RespondForm';
import type {
  PollDetail,
  PollFieldConfig,
  PollFieldInput,
  PollFieldOptionInput,
  PollFieldType,
  PollResultsVisibility,
} from './api';
import {
  createPoll,
  isAnswerableFieldType,
  publishPoll,
  toPollSlug,
  updatePoll,
  uploadPollImage,
} from './api';

const FIELD_TYPES: readonly { value: PollFieldType; label: string }[] = [
  { value: 'single_choice', label: 'Single choice' },
  { value: 'multi_choice', label: 'Multiple choice' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'short_text', label: 'Short text' },
  { value: 'long_text', label: 'Long text' },
  { value: 'scale', label: 'Scale' },
  { value: 'number', label: 'Number' },
  { value: 'number_range', label: 'Number range' },
  { value: 'date', label: 'Date' },
  { value: 'date_range', label: 'Date range' },
  { value: 'time', label: 'Time' },
  { value: 'datetime', label: 'Date and time' },
  { value: 'section', label: 'Section' },
  { value: 'content', label: 'Content' },
  { value: 'image', label: 'Image' },
];

const ELEMENT_ICONS: Partial<Record<PollFieldType, string>> = {
  single_choice: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8 M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20',
  multi_choice: 'M4 4h16v16H4z M7 12l3 3 7-7',
  dropdown: 'M3 5h18v14H3z M8 10l4 4 4-4',
  short_text: 'M4 5h16 M12 5v14 M8 19h8',
  long_text: 'M4 5h16 M4 10h16 M4 15h16 M4 20h10',
  scale: 'M3 12h18 M3 8v8 M9 10v4 M15 10v4 M21 8v8',
  number: 'M9 3L7 21 M17 3l-2 18 M3 9h18 M2 15h18',
  number_range: 'M3 8v8 M21 8v8 M3 12h18 M7 8l-4 4 4 4 M17 8l4 4-4 4',
  date: 'M4 5h16v16H4z M4 10h16 M8 3v4 M16 3v4 M8 14h2 M14 14h2',
  date_range: 'M4 5h16v16H4z M4 10h16 M8 3v4 M16 3v4 M7 15h10',
  time: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20 M12 6v6l4 2',
  datetime:
    'M3 4h14v7 M3 4v16h7 M3 9h14 M7 2v4 M13 2v4 M17 12a5 5 0 1 0 0 10 5 5 0 0 0 0-10 M17 14v3l2 1',
  section: 'M3 4h18v5H3z M3 14h18 M3 19h18',
  content: 'M4 4h16v16H4z M8 9h8 M8 13h8 M8 17h5',
  image: 'M3 5h18v14H3z M3 16l5-5 4 4 3-3 6 6 M15.5 8.5h.01',
};

const DEFAULT_LABELS: Partial<Record<PollFieldType, string>> = {
  section: 'New section',
  content: 'Content',
  image: 'Image',
};

const ElementTypePicker = (props: {
  label: string;
  value: PollFieldType;
  compact?: boolean;
  onChange: (value: PollFieldType) => void;
}) => (
  <fieldset class="min-w-0">
    <legend class="mb-3 text-sm font-semibold">{props.label}</legend>
    <div
      class={
        props.compact
          ? 'grid grid-cols-2 gap-2'
          : 'grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4'
      }
    >
      <For each={FIELD_TYPES}>
        {(type) => (
          <button
            type="button"
            aria-pressed={props.value === type.value}
            onClick={() => props.onChange(type.value)}
            class="flex min-h-16 items-center gap-3 rounded-xl border px-3 py-3 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            classList={{
              'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-200':
                props.value === type.value,
              'border-neutral-200 bg-white text-neutral-700 hover:border-brand-400 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200':
                props.value !== type.value,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              class="h-5 w-5 shrink-0"
            >
              <path d={ELEMENT_ICONS[type.value]} />
            </svg>
            <span>{type.label}</span>
          </button>
        )}
      </For>
    </div>
  </fieldset>
);

interface BuilderOption extends PollFieldOptionInput {
  id: string;
  label: string;
  jumpToSectionId: string | 'submit' | null;
}

interface BuilderField extends PollFieldInput {
  id: string;
  helpText: string;
  required: boolean;
  config: PollFieldConfig;
  options: BuilderOption[];
}

const isChoice = (type: PollFieldType) =>
  type === 'single_choice' || type === 'multi_choice' || type === 'dropdown';
const canBranch = (type: PollFieldType) => type === 'single_choice' || type === 'dropdown';

const newField = (type: PollFieldType): BuilderField => ({
  id: crypto.randomUUID(),
  type,
  label: DEFAULT_LABELS[type] ?? 'New question',
  helpText: '',
  required: false,
  config: type === 'scale' ? { min: 1, max: 5 } : {},
  options: isChoice(type)
    ? [
        { id: crypto.randomUUID(), label: 'Option 1', jumpToSectionId: null },
        { id: crypto.randomUUID(), label: 'Option 2', jumpToSectionId: null },
      ]
    : [],
});

export const PollBuilder = (props: {
  poll?: PollDetail;
  onClose: () => void;
  onSaved?: (pollId: string) => void;
}) => {
  const existing = props.poll;
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [title, setTitle] = createSignal(existing?.title ?? '');
  const [description, setDescription] = createSignal(existing?.description ?? '');
  // The link follows the title until the author edits it; the server derives it when untouched.
  const [slug, setSlug] = createSignal(existing?.slug ?? '');
  const [slugTouched, setSlugTouched] = createSignal(false);
  // Field ids with an image upload in flight; saving waits for all of them.
  const [uploading, setUploading] = createSignal<ReadonlySet<string>>(new Set());
  const setFieldUploading = (id: string, busy: boolean) =>
    setUploading((current) => {
      const next = new Set(current);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  const [isAnonymous, setIsAnonymous] = createSignal(existing?.isAnonymous ?? false);
  const [allowEdit, setAllowEdit] = createSignal(existing?.allowEdit ?? true);
  const [resultsVisibility, setResultsVisibility] = createSignal<PollResultsVisibility>(
    existing?.resultsVisibility ?? 'live',
  );
  const [closesAt, setClosesAt] = createSignal(localDateTime(existing?.closesAt ?? null));
  const [fieldStore, setFieldStore] = createStore<{ fields: BuilderField[] }>({
    fields:
      existing?.fields.map((field) => ({
        id: field.id,
        type: field.type,
        label: field.label,
        helpText: field.helpText,
        required: field.required,
        config: { ...field.config },
        options: field.options.map((option) => ({
          id: option.id,
          label: option.label,
          jumpToSectionId: option.jumpToSectionId,
        })),
      })) ?? [],
  });
  const fields = () => fieldStore.fields;
  const setFields = (update: (all: BuilderField[]) => BuilderField[]) =>
    setFieldStore('fields', reconcile(update(fields()), { key: 'id' }));
  const [preview, setPreview] = createSignal(false);
  const [addingType, setAddingType] = createSignal<PollFieldType>('short_text');
  const [addOpen, setAddOpen] = createSignal(false);
  let addTrigger: HTMLButtonElement | undefined;
  let addPanel: HTMLElement | undefined;
  createEffect(() => {
    if (!addOpen()) return;
    const outside = (event: MouseEvent) => {
      if (!addPanel?.contains(event.target as Node) && !addTrigger?.contains(event.target as Node))
        setAddOpen(false);
    };
    document.addEventListener('click', outside, true);
    onCleanup(() => document.removeEventListener('click', outside, true));
  });
  const [changingField, setChangingField] = createSignal<BuilderField>();
  let typeDialog: HTMLDialogElement | undefined;
  let typeTrigger: HTMLButtonElement | undefined;
  const [saving, setSaving] = createSignal(false);
  const [selected, setSelected] = createSignal<string | null>(null);
  const [savedId, setSavedId] = createSignal(existing?.id);
  const [complete, setComplete] = createSignal(false);
  const snapshot = () =>
    JSON.stringify({
      title: title(),
      description: description(),
      slug: slug(),
      isAnonymous: isAnonymous(),
      allowEdit: allowEdit(),
      resultsVisibility: resultsVisibility(),
      closesAt: closesAt(),
      fields: fields(),
    });
  const initial = snapshot();
  const dirty = () => !complete() && snapshot() !== initial;
  useBeforeLeave((event) => {
    if (!dirty() && !saving()) return;
    event.preventDefault();
    if (saving()) return;
    void confirm({ title: 'Discard unsaved changes?', confirmLabel: 'Discard', danger: true }).then(
      (ok) => {
        if (ok) event.retry(true);
      },
    );
  });
  const beforeUnload = (event: BeforeUnloadEvent) => {
    if (dirty() || saving()) {
      event.preventDefault();
      event.returnValue = '';
    }
  };
  window.addEventListener('beforeunload', beforeUnload);
  onCleanup(() => window.removeEventListener('beforeunload', beforeUnload));

  const updateField = (id: string, patch: Partial<BuilderField>) =>
    setFields((all) => all.map((field) => (field.id === id ? { ...field, ...patch } : field)));
  const updateOption = (fieldId: string, optionId: string, patch: Partial<BuilderOption>) =>
    setFields((all) =>
      all.map((field) =>
        field.id === fieldId
          ? {
              ...field,
              options: field.options.map((option) =>
                option.id === optionId ? { ...option, ...patch } : option,
              ),
            }
          : field,
      ),
    );
  const move = (index: number, delta: number) => {
    const next = [...fields()];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target] as BuilderField, next[index] as BuilderField];
    setFields(() => next);
  };

  const save = async (shouldPublish: boolean) => {
    if (saving()) return;
    if (uploading().size > 0) {
      toast.error('Wait for image uploads to finish');
      return;
    }
    if (!validDateTime(closesAt())) {
      toast.error('Choose a valid closing date and time');
      return;
    }
    if (!title().trim()) {
      toast.error('Poll title is required');
      return;
    }
    if (slugTouched() && !PollSlugSchema.safeParse(slug()).success) {
      toast.error('Link may use only lowercase letters, numbers, and single hyphens');
      return;
    }
    if (fields().some((field) => !field.label.trim())) {
      toast.error('Every field needs a label');
      return;
    }
    if (
      shouldPublish &&
      !(await confirm({
        title: 'Publish this poll?',
        body: 'Questions, options, branching, and response settings will be frozen. The description stays editable.',
        confirmLabel: 'Publish',
      }))
    )
      return;

    setSaving(true);
    try {
      const common = {
        title: title(),
        description: description(),
        ...(slugTouched() ? { slug: slug() } : {}),
        allowEdit: allowEdit(),
        resultsVisibility: resultsVisibility(),
        closesAt:
          closesAt() === localDateTime(existing?.closesAt ?? null)
            ? (existing?.closesAt ?? null)
            : closesAt()
              ? new Date(closesAt()).toISOString()
              : null,
        fields: fields(),
      };
      const currentId = savedId();
      const pollId = currentId
        ? (
            await apiCall(updatePoll, {
              params: { pollId: currentId },
              body: common,
            })
          ).data.id
        : (
            await apiCall(createPoll, {
              body: { ...common, isAnonymous: isAnonymous() },
            })
          ).data.id;
      setSavedId(pollId);
      if (shouldPublish) await apiCall(publishPoll, { params: { pollId } });
      await qc.invalidateQueries({ queryKey: ['polls'] });
      toast.success(shouldPublish ? 'Poll published' : 'Draft saved');
      setComplete(true);
      setSaving(false);
      if (props.onSaved) props.onSaved(pollId);
      else props.onClose();
    } catch (error) {
      toast.error(error instanceof ApiClientError ? error.error.message : 'Could not save poll');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <PageHeader
        title={preview() ? title() || 'Untitled poll' : existing ? 'Edit Poll' : 'New Poll'}
        actions={
          <div class="flex max-w-[62vw] flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              disabled={saving()}
              aria-pressed={preview()}
              onClick={() => {
                setAddOpen(false);
                setPreview(!preview());
              }}
            >
              {preview() ? 'Back to editor' : 'Preview'}
            </Button>
            <Button variant="secondary" disabled={saving()} onClick={props.onClose}>
              Back to Polls
            </Button>
            <Button loading={saving()} onClick={() => save(false)}>
              Save draft
            </Button>
            <Button variant="success" loading={saving()} onClick={() => save(true)}>
              Publish
            </Button>
          </div>
        }
      />
      <Show when={preview()}>
        <section aria-label="Form preview" class="mx-auto w-full max-w-6xl p-6">
          <PollPreview
            poll={{
              id: savedId() ?? 'preview',
              description: description(),
              allowEdit: allowEdit(),
              fields: fields().map((field) => ({
                ...field,
                options: field.options.map((option) => ({ ...option, addedBy: null })),
              })),
            }}
          />
        </section>
      </Show>
      <fieldset
        hidden={preview()}
        classList={{ hidden: preview() }}
        disabled={saving()}
        class="mx-auto flex w-full min-w-0 max-w-6xl flex-col gap-6 p-6"
      >
        <p class="text-sm text-neutral-500">
          Form editor — edit the labeled fields below, then save your draft or publish.
        </p>
        <section
          aria-label="Poll details"
          class="space-y-4 rounded-xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900"
        >
          <h2 class="font-semibold">Poll details</h2>
          <Input
            label="Title"
            placeholder="Poll title"
            required
            value={title()}
            onInput={(e) => {
              setTitle(e.currentTarget.value);
              if (!slugTouched() && !existing) setSlug(toPollSlug(e.currentTarget.value));
            }}
          />
          <Input
            label="Share link"
            placeholder="survey-example"
            hint={`${window.location.origin}/to-polls/${slug() || '…'}`}
            value={slug()}
            onInput={(e) => {
              setSlugTouched(true);
              setSlug(e.currentTarget.value.toLowerCase());
            }}
          />
          <MarkdownEditor
            label="Description"
            placeholder="Add a description"
            value={description()}
            onChange={setDescription}
          />
        </section>

        <details class="animated-card group rounded-xl border border-neutral-200 bg-surface dark:border-neutral-800 dark:bg-surface-dark">
          <summary class="flex cursor-pointer list-none items-center justify-between gap-4 rounded-xl bg-neutral-50 px-5 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 group-open:rounded-b-none group-open:border-b group-open:border-neutral-200 dark:bg-neutral-900 dark:group-open:border-neutral-800 [&::-webkit-details-marker]:hidden">
            <div>
              <h2 class="font-semibold">Poll settings</h2>
              <p class="mt-1 text-sm text-neutral-500">
                Manage responses, results access, and the closing deadline.
              </p>
            </div>
            <Icon
              name="angle-small-down"
              class="shrink-0 text-neutral-500 transition-transform group-open:rotate-180 motion-reduce:transition-none"
            />
          </summary>
          <div class="grid gap-6 p-5 lg:grid-cols-2">
            <div class="space-y-5">
              <div>
                <h3 class="mb-3 text-sm font-semibold">Response preferences</h3>
                <div class="divide-y divide-neutral-200 rounded-lg border border-neutral-200 dark:divide-neutral-800 dark:border-neutral-800">
                  <div class="p-4">
                    <Checkbox
                      label="Anonymous responses"
                      checked={isAnonymous()}
                      disabled={Boolean(savedId())}
                      onChange={setIsAnonymous}
                    />
                    <p class="mt-1 pl-6 text-xs text-neutral-500">
                      Hide respondent identities. This cannot be changed after the first save.
                    </p>
                  </div>
                  <div class="p-4">
                    <Checkbox
                      label="Allow edits and withdrawal"
                      checked={allowEdit()}
                      onChange={setAllowEdit}
                    />
                    <p class="mt-1 pl-6 text-xs text-neutral-500">
                      Let respondents update or withdraw their response while the poll is open.
                    </p>
                  </div>
                </div>
              </div>
              <div class="space-y-2">
                <Select
                  label="Results visibility"
                  value={resultsVisibility()}
                  onChange={(value) => setResultsVisibility(value as PollResultsVisibility)}
                  options={[
                    { value: 'live', label: 'Everyone — live' },
                    { value: 'after_close', label: 'Everyone — after close' },
                    { value: 'restricted', label: 'Dashboard viewers only' },
                  ]}
                />
                <p class="text-xs text-neutral-500">
                  Choose who can see results and when they become available.
                </p>
              </div>
            </div>
            <div class="min-w-0 space-y-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900">
              <DateTimePicker label="Closes at" value={closesAt()} onChange={setClosesAt} />
              <p class="border-t border-neutral-200 pt-3 text-xs text-neutral-500 dark:border-neutral-800">
                Optional — leave unset to close the poll manually.
              </p>
            </div>
          </div>
        </details>

        <div class="fixed bottom-4 right-4 z-30">
          <Button
            ref={addTrigger}
            aria-expanded={addOpen()}
            aria-controls="poll-add-element"
            onClick={() => setAddOpen(!addOpen())}
          >
            Add an element
          </Button>
        </div>
        <aside
          ref={addPanel}
          aria-label="Add element"
          id="poll-add-element"
          inert={!addOpen()}
          aria-hidden={!addOpen()}
          classList={{
            'visible translate-y-0 opacity-100': addOpen(),
            'invisible translate-y-4 opacity-0': !addOpen(),
          }}
          class="transition-[opacity,translate,visibility] duration-200 ease-out motion-reduce:transition-none fixed bottom-20 right-4 z-30 flex max-h-[calc(100dvh-10rem)] w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-brand-200 bg-surface shadow-xl dark:border-brand-900 dark:bg-surface-dark"
        >
          <header class="shrink-0 border-b border-brand-200 px-5 py-4 dark:border-brand-900">
            <div class="flex items-center justify-between">
              <h2 class="font-semibold">Add an element</h2>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Close add element"
                onClick={() => {
                  setAddOpen(false);
                  addTrigger?.focus();
                }}
              >
                ✕
              </Button>
            </div>
            <p class="mt-1 text-sm text-neutral-500">
              Choose an element type, then press Add to insert it into your form.
            </p>
          </header>
          <div class="overflow-y-auto p-4">
            <ElementTypePicker
              compact
              label="Element type"
              value={addingType()}
              onChange={setAddingType}
            />
          </div>
          <footer class="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-brand-200 px-5 py-4 dark:border-brand-900">
            <p class="text-sm text-neutral-600 dark:text-neutral-300">
              Selected:{' '}
              <strong>{FIELD_TYPES.find((type) => type.value === addingType())?.label}</strong>
            </p>
            <Button
              onClick={() => {
                const field = newField(addingType());
                setFields((all) => [...all, field]);
                setSelected(field.id);
                queueMicrotask(() => {
                  const card = document.getElementById(`poll-field-${field.id}`);
                  card
                    ?.querySelector<HTMLInputElement>('input:not([type]), input[type="text"]')
                    ?.focus({ preventScroll: true });
                  card?.scrollIntoView({
                    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                      ? 'instant'
                      : 'smooth',
                    block: 'start',
                  });
                });
              }}
            >
              Add
            </Button>
          </footer>
        </aside>

        <div class="flex flex-col gap-3">
          <For each={fields()}>
            {(field, index) => {
              let menuContainer: HTMLDivElement | undefined;
              const laterSections = () =>
                fields()
                  .slice(index() + 1)
                  .filter((candidate) => candidate.type === 'section')
                  .map((candidate) => ({ value: candidate.id, label: candidate.label }));
              return (
                <section
                  id={`poll-field-${field.id}`}
                  onFocusIn={() => setSelected(field.id)}
                  onClick={() => setSelected(field.id)}
                  aria-label={`Question ${index() + 1}`}
                  class="scroll-mt-24 overflow-hidden rounded-xl border border-neutral-200 bg-surface dark:border-neutral-800 dark:bg-surface-dark"
                  classList={{ 'ring-2 ring-brand-500': selected() === field.id }}
                >
                  <header class="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-neutral-50 px-5 py-4 dark:border-neutral-800 dark:bg-neutral-900">
                    <div>
                      <h3 class="font-semibold">Question {index() + 1}</h3>
                      <p class="mt-1 text-xs text-neutral-500">
                        {FIELD_TYPES.find((type) => type.value === field.type)?.label}
                      </p>
                    </div>
                    <div class="flex flex-wrap items-center justify-between gap-3">
                      <Show when={isAnswerableFieldType(field.type)}>
                        <Checkbox
                          label="Required"
                          checked={field.required}
                          onChange={(required) => updateField(field.id, { required })}
                        />
                      </Show>
                      <div class="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label="Move question up"
                          disabled={index() === 0}
                          onClick={() => move(index(), -1)}
                        >
                          ↑
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label="Move question down"
                          disabled={index() === fields().length - 1}
                          onClick={() => move(index(), 1)}
                        >
                          ↓
                        </Button>
                        <div ref={menuContainer}>
                          <Menu
                            trigger={
                              <>
                                <span aria-hidden="true">⋯</span>
                                <span class="sr-only">Question {index() + 1} actions</span>
                              </>
                            }
                            items={[
                              {
                                label: 'Change type',
                                disabled: saving(),
                                onSelect: () => {
                                  typeTrigger =
                                    menuContainer?.querySelector<HTMLButtonElement>('button') ??
                                    undefined;
                                  typeTrigger?.focus();
                                  setSelected(field.id);
                                  setChangingField(field);
                                  typeDialog?.showModal();
                                },
                              },
                              {
                                label: 'Remove',
                                danger: true,
                                disabled: saving(),
                                onSelect: () =>
                                  setFields((all) => all.filter((item) => item.id !== field.id)),
                              },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  </header>
                  <div class="flex flex-col gap-4 p-5">
                    <Show when={field.type === 'content'}>
                      <MarkdownEditor
                        label="Content"
                        placeholder="Write markdown shown to respondents"
                        value={field.config.markdown ?? ''}
                        onChange={(markdown) =>
                          updateField(field.id, { config: { ...field.config, markdown } })
                        }
                      />
                    </Show>
                    <Show when={field.type === 'image'}>
                      <div class="flex flex-col gap-3">
                        <PollDisplayElement field={{ ...field, helpText: '', options: [] }} />
                        <label class="self-start">
                          <span class="sr-only">Upload image</span>
                          <input
                            type="file"
                            accept={POLL_IMAGE_CONTENT_TYPES.join(',')}
                            class="sr-only"
                            disabled={uploading().has(field.id)}
                            onChange={async (e) => {
                              const input = e.currentTarget;
                              const file = input.files?.[0];
                              input.value = '';
                              if (!file) return;
                              if (
                                !(POLL_IMAGE_CONTENT_TYPES as readonly string[]).includes(
                                  file.type,
                                ) ||
                                file.size > POLL_IMAGE_MAX_BYTES
                              ) {
                                toast.error('Choose a JPEG, PNG, or WebP image up to 5 MB');
                                return;
                              }
                              setFieldUploading(field.id, true);
                              try {
                                const imagePath = await uploadPollImage(file);
                                updateField(field.id, {
                                  config: {
                                    ...field.config,
                                    imagePath,
                                    imageUrl: URL.createObjectURL(file),
                                  },
                                });
                              } catch {
                                toast.error('Could not upload image');
                              } finally {
                                setFieldUploading(field.id, false);
                              }
                            }}
                          />
                          <span
                            aria-hidden="true"
                            class="inline-flex h-9 cursor-pointer items-center rounded-lg border border-neutral-300 bg-white px-3 text-sm font-medium transition-colors hover:bg-neutral-50 motion-reduce:transition-none dark:border-neutral-700 dark:bg-neutral-900 dark:hover:bg-neutral-800"
                          >
                            {uploading().has(field.id)
                              ? 'Uploading…'
                              : field.config.imagePath
                                ? 'Replace image'
                                : 'Upload image'}
                          </span>
                        </label>
                      </div>
                    </Show>
                    <Show when={field.type !== 'content' && field.type !== 'image'}>
                      <div class="flex items-center gap-1">
                        <div class="min-w-0 flex-1">
                          <Input
                            label={field.type === 'section' ? 'Section title' : 'Question'}
                            class="font-medium"
                            required
                            value={field.label}
                            onInput={(e) => updateField(field.id, { label: e.currentTarget.value })}
                          />
                        </div>
                        <Show when={field.required && isAnswerableFieldType(field.type)}>
                          <span aria-label="Required question" class="text-red-500">
                            *
                          </span>
                        </Show>
                      </div>
                    </Show>
                    <Show when={field.type !== 'content'}>
                      <Input
                        label={field.type === 'image' ? 'Caption' : 'Help text'}
                        placeholder={field.type === 'image' ? 'Add a caption' : 'Add help text'}
                        class="min-w-0"
                        value={field.helpText}
                        onInput={(e) => updateField(field.id, { helpText: e.currentTarget.value })}
                      />
                    </Show>

                    <Show
                      when={
                        isChoice(field.type) &&
                        (field.type !== 'dropdown' || selected() === field.id)
                      }
                    >
                      <div class="flex flex-col gap-2">
                        <For each={field.options}>
                          {(option) => (
                            <div class="flex flex-wrap items-end gap-2">
                              <span
                                aria-hidden="true"
                                class="mb-3 h-4 w-4 shrink-0 border border-neutral-300 dark:border-neutral-600"
                                classList={{
                                  'rounded-full': field.type === 'single_choice',
                                  rounded: field.type !== 'single_choice',
                                }}
                              />
                              <div class="min-w-0 flex-1">
                                <Input
                                  label="Option label"
                                  class="min-w-0"
                                  value={option.label}
                                  onInput={(e) =>
                                    updateOption(field.id, option.id, {
                                      label: e.currentTarget.value,
                                    })
                                  }
                                />
                              </div>
                              <Show when={selected() === field.id && canBranch(field.type)}>
                                <Select
                                  label="After this answer"
                                  value={option.jumpToSectionId ?? ''}
                                  onChange={(value) =>
                                    updateOption(field.id, option.id, {
                                      jumpToSectionId: value === '' ? null : value,
                                    })
                                  }
                                  options={[
                                    { value: '', label: 'Continue' },
                                    ...laterSections(),
                                    { value: 'submit', label: 'Submit form' },
                                  ]}
                                />
                              </Show>
                              <Button
                                classList={{ invisible: selected() !== field.id }}
                                class="h-10 shrink-0"
                                aria-label="Remove option"
                                size="md"
                                variant="ghost"
                                onClick={() =>
                                  updateField(field.id, {
                                    options: field.options.filter((item) => item.id !== option.id),
                                  })
                                }
                              >
                                Remove
                              </Button>
                            </div>
                          )}
                        </For>
                        <Button
                          classList={{ hidden: selected() !== field.id }}
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            updateField(field.id, {
                              options: [
                                ...field.options,
                                {
                                  id: crypto.randomUUID(),
                                  label: `Option ${field.options.length + 1}`,
                                  jumpToSectionId: null,
                                },
                              ],
                            })
                          }
                        >
                          Add option
                        </Button>
                        <Show when={selected() === field.id}>
                          <Checkbox
                            label="Respondents can add options"
                            checked={field.config.allowAddOption ?? false}
                            onChange={(allowAddOption) =>
                              updateField(field.id, { config: { ...field.config, allowAddOption } })
                            }
                          />
                        </Show>
                      </div>
                    </Show>

                    <Show
                      when={
                        (!isChoice(field.type) ||
                          (field.type === 'dropdown' && selected() !== field.id)) &&
                        isAnswerableFieldType(field.type)
                      }
                    >
                      <div>
                        <p class="mb-2 text-xs font-medium text-neutral-500">
                          Answer preview — completed by respondents
                        </p>
                        <div inert class="pointer-events-none">
                          <PollQuestion
                            embedded
                            pollId={savedId() ?? ''}
                            disabled
                            value={undefined}
                            onChange={() => {}}
                            field={{
                              ...field,
                              label: '',
                              helpText: '',
                              options: field.options.map((option) => ({
                                ...option,
                                addedBy: null,
                              })),
                            }}
                          />
                        </div>
                      </div>
                    </Show>
                  </div>
                  <Show
                    when={[
                      'multi_choice',
                      'scale',
                      'number',
                      'number_range',
                      'short_text',
                      'long_text',
                      'date_range',
                    ].includes(field.type)}
                  >
                    <footer class="space-y-4 border-t border-neutral-200 bg-neutral-50 px-5 py-4 dark:border-neutral-800 dark:bg-neutral-900">
                      <details class="animated-card" open={selected() === field.id}>
                        <summary class="cursor-pointer text-sm font-medium">
                          Element settings
                        </summary>
                        <div class="space-y-4 pt-4">
                          <div>
                            <Show when={field.type === 'multi_choice'}>
                              <div class="grid grid-cols-2 gap-2">
                                <Input
                                  label="Minimum selections"
                                  type="number"
                                  min="0"
                                  value={field.config.minSelections ?? ''}
                                  onInput={(e) =>
                                    updateField(field.id, {
                                      config: {
                                        ...field.config,
                                        minSelections: e.currentTarget.value
                                          ? Number(e.currentTarget.value)
                                          : undefined,
                                      },
                                    })
                                  }
                                />
                                <Input
                                  label="Maximum selections"
                                  type="number"
                                  min="1"
                                  value={field.config.maxSelections ?? ''}
                                  onInput={(e) =>
                                    updateField(field.id, {
                                      config: {
                                        ...field.config,
                                        maxSelections: e.currentTarget.value
                                          ? Number(e.currentTarget.value)
                                          : undefined,
                                      },
                                    })
                                  }
                                />
                              </div>
                            </Show>

                            <Show when={['scale', 'number', 'number_range'].includes(field.type)}>
                              <div class="grid grid-cols-2 gap-2">
                                <Input
                                  label="Minimum"
                                  type="number"
                                  value={field.config.min ?? ''}
                                  onInput={(e) =>
                                    updateField(field.id, {
                                      config: {
                                        ...field.config,
                                        min: e.currentTarget.value
                                          ? Number(e.currentTarget.value)
                                          : undefined,
                                      },
                                    })
                                  }
                                />
                                <Input
                                  label="Maximum"
                                  type="number"
                                  value={field.config.max ?? ''}
                                  onInput={(e) =>
                                    updateField(field.id, {
                                      config: {
                                        ...field.config,
                                        max: e.currentTarget.value
                                          ? Number(e.currentTarget.value)
                                          : undefined,
                                      },
                                    })
                                  }
                                />
                              </div>
                            </Show>

                            <Show when={field.type === 'date_range'}>
                              <div class="grid gap-3 sm:grid-cols-2">
                                <DatePicker
                                  label="Minimum date"
                                  value={field.config.minDate ?? ''}
                                  max={field.config.maxDate}
                                  onChange={(minDate) =>
                                    updateField(field.id, {
                                      config: { ...field.config, minDate: minDate || undefined },
                                    })
                                  }
                                />
                                <DatePicker
                                  label="Maximum date"
                                  value={field.config.maxDate ?? ''}
                                  min={field.config.minDate}
                                  onChange={(maxDate) =>
                                    updateField(field.id, {
                                      config: { ...field.config, maxDate: maxDate || undefined },
                                    })
                                  }
                                />
                              </div>
                            </Show>
                            <Show when={field.type === 'short_text' || field.type === 'long_text'}>
                              <Input
                                label="Maximum characters"
                                type="number"
                                min="1"
                                value={field.config.maxLength ?? ''}
                                onInput={(e) =>
                                  updateField(field.id, {
                                    config: {
                                      ...field.config,
                                      maxLength: e.currentTarget.value
                                        ? Number(e.currentTarget.value)
                                        : undefined,
                                    },
                                  })
                                }
                              />
                            </Show>
                          </div>
                        </div>
                      </details>
                    </footer>
                  </Show>
                </section>
              );
            }}
          </For>
        </div>
      </fieldset>
      <dialog
        ref={typeDialog}
        aria-label="Choose question type"
        class="m-auto max-h-[calc(100dvh-2rem)] w-[min(48rem,calc(100vw-2rem))] overflow-y-auto rounded-xl border border-neutral-200 bg-surface p-5 text-neutral-900 shadow-xl backdrop:bg-black/40 dark:border-neutral-700 dark:bg-surface-dark dark:text-neutral-100"
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="font-semibold">Change question type</h2>
          <Button
            variant="ghost"
            aria-label="Close question type"
            onClick={() => typeDialog?.close()}
          >
            ✕
          </Button>
        </div>
        <Show when={changingField()}>
          {(field) => (
            <ElementTypePicker
              label="Question type"
              value={field().type}
              onChange={async (type) => {
                const current = field();
                typeDialog?.close();
                if (type === current.type) return;
                const accepted = await confirm({
                  title: 'Change question type?',
                  body: 'Type-specific settings and incompatible options will be reset.',
                  confirmLabel: 'Change type',
                });
                if (accepted) {
                  const defaults = newField(type);
                  updateField(current.id, {
                    type,
                    config: defaults.config,
                    label:
                      isAnswerableFieldType(type) === isAnswerableFieldType(current.type)
                        ? current.label
                        : defaults.label,
                    required: isAnswerableFieldType(type) ? current.required : false,
                    options:
                      isChoice(type) && isChoice(current.type)
                        ? current.options.map((option) => ({
                            ...option,
                            jumpToSectionId: canBranch(type) ? option.jumpToSectionId : null,
                          }))
                        : defaults.options,
                  });
                }
                typeTrigger?.focus();
              }}
            />
          )}
        </Show>
      </dialog>
    </section>
  );
};
